import { useApi } from "@/hooks/useApi";
import { useFavoritePrompts } from "@/hooks/usePromptsFavorite";
import { useTranslation } from "@/hooks/useTranslation";
import { PROMPT_EVENTS } from "@/lib/prompt-events";
import {
  getPromptId,
  getPromptSlug,
  getPromptUsageMap,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { PromptDTO } from "@shaarp/common";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SLASH_COMMAND_REGEX = /^\/(\S*)$/;

export type SlashCommandPromptGroup = {
  key: string;
  labelKey: string;
  prompts: PromptDTO[];
};

export function useSlashCommand(userInput: string) {
  const { i18n } = useTranslation();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [allPrompts, setAllPrompts] = useState<PromptDTO[]>([]);
  const mountedRef = useRef(true);
  const { favoriteIds } = useFavoritePrompts({ fetchCustomPrompts: false });

  const fetchPrompts = useCallback(async () => {
    try {
      const { data } = await apiRef.current.get<PromptDTO[]>("/prompts");
      if (mountedRef.current) setAllPrompts(data);
    } catch {
      // Silent fail — prompts just won't appear in autocomplete
    }
  }, []);

  // Fetch prompts on mount
  useEffect(() => {
    mountedRef.current = true;
    fetchPrompts();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchPrompts]);

  // Listen for prompt changes to refresh cache
  useEffect(() => {
    const handler = () => fetchPrompts();
    window.addEventListener(PROMPT_EVENTS.PROMPTS_CHANGED, handler);
    return () =>
      window.removeEventListener(PROMPT_EVENTS.PROMPTS_CHANGED, handler);
  }, [fetchPrompts]);

  const [dismissed, setDismissed] = useState(false);
  const prevInputRef = useRef(userInput);

  // Reset dismissed flag when input changes
  useEffect(() => {
    if (userInput !== prevInputRef.current) {
      setDismissed(false);
      prevInputRef.current = userInput;
    }
  }, [userInput]);

  const dismiss = useCallback(() => setDismissed(true), []);

  // Detect slash command pattern
  const slashMatch = userInput.match(SLASH_COMMAND_REGEX);
  const isActive = !!slashMatch && !dismissed;
  const query = slashMatch ? slashMatch[1] : "";

  // Filter, group, and sort prompts
  const promptGroups = useMemo((): SlashCommandPromptGroup[] => {
    if (!isActive) return [];

    const lang = getSupportedLang(i18n.language);
    const normalizedQuery = query.toLowerCase();
    const usageMap = getPromptUsageMap();

    const matched = allPrompts.filter((prompt) => {
      const slug = getPromptSlug(prompt, lang);
      if (!slug) return false;
      return slug.includes(normalizedQuery);
    });

    const sortByLastUsed = (a: PromptDTO, b: PromptDTO): number => {
      const aUsed = usageMap[getPromptId(a)] || "";
      const bUsed = usageMap[getPromptId(b)] || "";
      return bUsed.localeCompare(aUsed);
    };

    const favorites = matched
      .filter((p) => favoriteIds.has(getPromptId(p)))
      .sort(sortByLastUsed);
    const others = matched
      .filter((p) => !favoriteIds.has(getPromptId(p)))
      .sort(sortByLastUsed);

    const groups: SlashCommandPromptGroup[] = [];
    if (favorites.length > 0) {
      groups.push({
        key: "favorites",
        labelKey: "chat.input.slashCommand.favorites",
        prompts: favorites,
      });
    }
    if (others.length > 0) {
      groups.push({
        key: "others",
        labelKey: "chat.input.slashCommand.others",
        prompts: others,
      });
    }

    return groups;
  }, [isActive, query, allPrompts, i18n.language, favoriteIds]);

  const totalCount = useMemo(
    () => promptGroups.reduce((sum, g) => sum + g.prompts.length, 0),
    [promptGroups],
  );

  return { isActive, query, promptGroups, totalCount, dismiss };
}
